import './styles/app.css';

import { MsalProvider } from '@azure/msal-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { createQueryClient } from './api/queries';
import { App } from './App';
import { createMsalInstance, entraConfigFromEnv } from './auth/msal';
import { env } from './env';

import type { ReactNode } from 'react';

/*
 * Boot: mock mode (MSW + a mock session) or MSAL, whose redirect must be finished before the router
 * decides where to go. No analytics or telemetry of any kind is loaded.
 */

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root element');
const root = createRoot(container);
const queryClient = createQueryClient();

function mount(children: ReactNode): void {
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </StrictMode>,
  );
}

async function boot(): Promise<void> {
  // A build-time literal: production bundles drop this branch, MSW and its worker script with it.
  if (import.meta.env.VITE_API_MOCK === '1') {
    const [{ enableMockApi }, { createMockAuth }] = await Promise.all([
      import('./api/mock/browser'),
      import('./auth/mockAuth'),
    ]);
    await enableMockApi();
    mount(<App entra={null} queryClient={queryClient} mockAuth={createMockAuth()} />);
    return;
  }
  const entra = entraConfigFromEnv(env);
  const pca = entra ? await createMsalInstance(entra) : null;
  mount(
    entra && pca ? (
      <MsalProvider instance={pca}>
        <App entra={entra} queryClient={queryClient} />
      </MsalProvider>
    ) : (
      <App entra={null} queryClient={queryClient} />
    ),
  );
}

/** Never a blank page: a plain message (styled by the stylesheet, nothing inline) and Reload. */
function renderBootFailure(): void {
  const main = document.createElement('main');
  main.className = 'boot-failure';
  main.setAttribute('role', 'alert');
  const message = document.createElement('p');
  message.textContent = navigator.onLine
    ? "Your Direwolf AI account page couldn't start. Please reload the page."
    : "You're offline. Reload the page when you're back online.";
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'btn btn-primary';
  button.textContent = 'Reload';
  button.addEventListener('click', () => {
    window.location.reload();
  });
  main.append(message, button);
  container?.replaceChildren(main);
}

boot().catch(renderBootFailure);
